/**
 * VoiceAgentPopup - Main popup component for voice agent
 */

import { motion } from 'framer-motion'
import React, { useCallback, useState } from 'react'

import { AudioVisualizer } from './components/AudioVisualizer'
import { ChatInput } from './components/ChatInput'
import { MicrophoneControl } from './components/MicrophoneControl'
import { TranscriptView } from './components/TranscriptView'
import { ERRORS } from './lib/constants'
import type { VoiceAgentProps } from './lib/types'
import { adjustColorBrightness, checkBrowserSupport, requestMicrophonePermission } from './lib/utils'
import { useVoiceAgent, VoiceAgentProvider } from './VoiceAgentProvider'

/**
 * Inner popup component that uses the context
 */
export const VoiceAgentPopupInner: React.FC<{
  onClose?: () => void
}> = ({ onClose }) => {
  const {
    connectionState,
    agentState,
    transcript,
    isAgentSpeaking,
    isMicrophoneEnabled,
    microphoneLevel,
    availableMicrophones,
    selectedMicrophone,
    callState,
    disconnect,
    startCall,
    toggleMicrophone,
    selectMicrophone,
    sendChatMessage,
    config,
  } = useVoiceAgent()

  const [chatOpen, setChatOpen] = useState(false)
  const [permissionError, setPermissionError] = useState<string | null>(null)

  // Handle starting a call
  const handleStartCall = useCallback(async () => {
    console.log('[VoiceAgentPopup] Starting call...')

    // Check browser support
    const { supported, missing } = checkBrowserSupport()
    if (!supported) {
      setPermissionError(`Your browser doesn't support: ${missing.join(', ')}`)
      return
    }

    // Request microphone permission
    const hasPermission = await requestMicrophonePermission()
    if (!hasPermission) {
      setPermissionError(ERRORS.MICROPHONE_PERMISSION_DENIED)
      return
    }

    // Start the call
    console.log('[VoiceAgentPopup] Calling startCall()')
    await startCall()
  }, [startCall])

  // Handle close
  const handleClose = useCallback(() => {
    disconnect()
    onClose?.()
  }, [disconnect, onClose])

  // Show idle state ONLY when callState is idle AND autoConnect is NOT enabled
  // This ensures auto-connect scenarios (like internal app usage) skip the idle state
  const isIdle = callState === 'idle' && config.autoConnect !== true

  // Get position classes based on config.position
  const getPositionClasses = () => {
    switch (config.position) {
      case 'top-left':
        return 'tw-top-5 tw-left-5'
      case 'top-right':
        return 'tw-top-5 tw-right-5'
      case 'bottom-left':
        return 'tw-bottom-5 tw-left-5'
      case 'bottom-right':
      default:
        return 'tw-bottom-5 tw-right-5'
    }
  }

  return (
    <motion.div
      initial={{ height: '120px' }}
      animate={{ height: isIdle ? '80px' : chatOpen ? '480px' : '120px' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`tw-fixed ${getPositionClasses()} tw-z-[9999] tw-flex tw-w-[360px] tw-flex-col tw-rounded-[2px] tw-border tw-border-gray-300 tw-bg-white tw-shadow-xl`}
      style={{ overflow: 'visible' }}
    >
      {/* Permission error */}
      {permissionError && <div className='tw-m-2 tw-rounded-3xl tw-bg-red-50 tw-p-4 tw-text-sm tw-text-red-700'>{permissionError}</div>}

      {/* Idle State - "Voice Chat" button */}
      {isIdle && <IdleStateView onStartCall={handleStartCall} brandName={config.brandName} isLoading={connectionState === 'connecting'} primaryColor={config.primaryColor} />}

      {/* Active Call State */}
      {!isIdle && (
        <>
          {/* Main content area - transcript (only visible when chat open) */}
          {chatOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className='tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden'>
              <div className='tw-flex-1 tw-overflow-hidden tw-p-2'>
                <TranscriptView messages={transcript} showTimestamps={config.debugMode} maxHeight='100%' emptyStateMessage='Start speaking or type a message...' />
              </div>
            </motion.div>
          )}

          {/* Audio Visualizer - centered when chat closed */}
          {!chatOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className='tw-flex tw-flex-1 tw-items-center tw-justify-center tw-px-8 tw-py-4'>
              <AudioVisualizer isActive={isAgentSpeaking} barCount={5} size='small' color={config.primaryColor || '#4ade80'} />
            </motion.div>
          )}

          {/* Action Bar - ALWAYS AT BOTTOM */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: connectionState === 'connected' ? 1 : 0 }} transition={{ delay: 0.5 }} className='tw-shrink-0'>
            <div className='tw-mx-2 tw-mb-2 tw-flex tw-flex-col tw-rounded-[2px] tw-border tw-border-gray-300 tw-bg-gray-100 tw-p-1 tw-shadow-md'>
              {/* Chat input (slides in when chat is open) */}
              {config.allowChat !== false && (
                <div className={`tw-relative tw-overflow-hidden tw-transition-all tw-duration-300 ${chatOpen ? 'tw-h-[46px]' : 'tw-h-0'}`}>
                  <div
                    className={`tw-absolute tw-inset-x-0 tw-top-0 tw-flex tw-h-9 tw-w-full tw-transition-opacity tw-duration-150 tw-ease-linear ${chatOpen ? 'tw-opacity-100 tw-delay-150' : 'tw-opacity-0'}`}
                  >
                    <ChatInput onSend={sendChatMessage} disabled={connectionState !== 'connected'} placeholder='Type something...' autoFocus={false} />
                  </div>
                  {chatOpen && <hr className='tw-absolute tw-inset-x-0 tw-bottom-0 tw-my-1 tw-w-full tw-border-gray-300' />}
                </div>
              )}

              {/* Control buttons */}
              <div className='tw-flex tw-justify-between tw-gap-1'>
                <div className='tw-flex tw-gap-1'>
                  {/* Microphone control */}
                  <MicrophoneControl
                    isEnabled={isMicrophoneEnabled}
                    level={microphoneLevel}
                    onToggle={toggleMicrophone}
                    onDeviceSelect={selectMicrophone}
                    availableDevices={availableMicrophones}
                    selectedDevice={selectedMicrophone}
                    showLevel={false}
                    showDeviceSelector={availableMicrophones.length > 1}
                    disabled={connectionState !== 'connected'}
                    size='medium'
                    primaryColor={config.primaryColor}
                  />
                </div>

                <div className='tw-flex tw-gap-1'>
                  {/* Chat toggle button */}
                  {config.allowChat !== false && (
                    <button
                      type='button'
                      onClick={() => setChatOpen(!chatOpen)}
                      disabled={connectionState !== 'connected'}
                      className={`tw-flex tw-aspect-square tw-h-9 tw-items-center tw-justify-center tw-rounded-xl tw-border-none tw-text-lg tw-transition-colors tw-duration-200 ${
                        chatOpen ? 'tw-bg-blue-100 tw-text-blue-600' : 'tw-bg-gray-200 tw-text-gray-600'
                      } ${connectionState === 'connected' ? 'tw-cursor-pointer' : 'tw-cursor-not-allowed tw-opacity-50'}`}
                      aria-label='Toggle chat'
                      title='Toggle chat'
                    >
                      <ChatIcon />
                    </button>
                  )}

                  {/* End call button */}
                  <button
                    type='button'
                    onClick={handleClose}
                    className='tw-flex tw-aspect-square tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-border-none tw-bg-red-50 tw-text-red-600 tw-transition-colors tw-duration-200 hover:tw-bg-red-100'
                    aria-label='End call'
                    title='End call'
                  >
                    <PhoneHangUpIcon />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Debug panel (optional) */}
          {config.debugMode && (
            <div className='tw-absolute tw-bottom-16 tw-left-2 tw-right-2 tw-z-30 tw-rounded-lg tw-bg-gray-800 tw-p-2 tw-font-mono tw-text-xs tw-text-gray-300'>
              <div>Connection: {connectionState}</div>
              <div>Agent: {agentState}</div>
              <div>Messages: {transcript.length}</div>
              <div>Mic Level: {(microphoneLevel * 100).toFixed(0)}%</div>
            </div>
          )}
        </>
      )}
      <style>{`
        /* Scrollbar styling for transcript */
        .voice-agent-transcript {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
          transition: scrollbar-color 0.2s linear;
        }

        .voice-agent-transcript:hover {
          scrollbar-color: #707070 transparent;
        }

        .voice-agent-transcript::-webkit-scrollbar {
          width: 8px;
        }

        .voice-agent-transcript::-webkit-scrollbar-thumb {
          background-color: transparent;
          transition: background-color 0.2s linear;
          border-radius: 4px;
        }

        .voice-agent-transcript:hover::-webkit-scrollbar-thumb {
          background-color: #707070;
        }

        .voice-agent-transcript::-webkit-scrollbar-track {
          background-color: transparent;
        }

        /* Rotating gradient circle animation */
        @keyframes rotateGradient {
          0% {
            transform: rotate(0deg) scale(1);
            box-shadow: 0 0 0px color-mix(in srgb, var(--primary-color, #4ade80) 0%, transparent);
          }
          50% {
            transform: rotate(180deg) scale(1.05);
            box-shadow: 0 0 20px color-mix(in srgb, var(--primary-color, #4ade80) 40%, transparent);
          }
          100% {
            transform: rotate(360deg) scale(1);
            box-shadow: 0 0 0px color-mix(in srgb, var(--primary-color, #4ade80) 0%, transparent);
          }
        }

        @keyframes glowPulse {
          0% {
            opacity: 0;
            transform: scale(1);
          }
          50% {
            opacity: 0.3;
            transform: scale(1.1);
          }
          100% {
            opacity: 0;
            transform: scale(1);
          }
        }

        .rotating-gradient-circle {
          animation: rotateGradient 4s ease-in-out infinite;
          position: relative;
          background: linear-gradient(135deg, var(--primary-color, #4ade80), var(--darker-color, #064e3b));
        }

        .rotating-gradient-circle::before {
          content: '';
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary-color, #4ade80), var(--darker-color, #064e3b));
          filter: blur(4px);
          animation: glowPulse 4s ease-in-out infinite;
          z-index: -1;
        }
      `}</style>
    </motion.div>
  )
}

/**
 * Main VoiceAgentPopup component with provider
 */
export const VoiceAgentPopup: React.FC<VoiceAgentProps> = (props) => {
  return (
    <VoiceAgentProvider {...props}>
      <VoiceAgentPopupInner onClose={props.onClose} />
    </VoiceAgentProvider>
  )
}

/**
 * ChatIcon - Modern chat bubble icon (Phosphor Icons style)
 */
const ChatIcon: React.FC = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 256 256' fill='currentColor'>
    <path d='M88,104a8,8,0,0,1,8-8h64a8,8,0,0,1,0,16H96A8,8,0,0,1,88,104Zm8,40h64a8,8,0,0,0,0-16H96a8,8,0,0,0,0,16ZM232,56V184a16,16,0,0,1-16,16H155.57l-13.68,23.94a16,16,0,0,1-27.78,0L100.43,200H40a16,16,0,0,1-16-16V56A16,16,0,0,1,40,40H216A16,16,0,0,1,232,56Zm-16,0H40V184h65.07a8,8,0,0,1,7,4l16,28,16-28a8,8,0,0,1,7-4H216Z' />
  </svg>
)

/**
 * PhoneHangUpIcon - Phone hang up icon (Phosphor Icons style)
 */
const PhoneHangUpIcon: React.FC = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 256 256' fill='currentColor'>
    <path d='M231.59,90.13h0C175.44,34,80.56,34,24.41,90.13c-20,20-21.92,49.49-4.69,71.71A16,16,0,0,0,32.35,168a15.8,15.8,0,0,0,5.75-1.08l49-17.37.29-.11a16,16,0,0,0,9.75-11.73l5.9-29.52a76.52,76.52,0,0,1,49.68-.11h0l6.21,29.75a16,16,0,0,0,9.72,11.59l.29.11,49,17.39a16,16,0,0,0,18.38-5.06C253.51,139.62,251.58,110.13,231.59,90.13ZM223.67,152l-.3-.12-48.82-17.33-6.21-29.74A16,16,0,0,0,158,93a92.56,92.56,0,0,0-60.34.13,16,16,0,0,0-10.32,12l-5.9,29.51L32.63,151.86c-.1,0-.17.13-.27.17-12.33-15.91-11-36.23,3.36-50.58,25-25,58.65-37.53,92.28-37.53s67.27,12.51,92.28,37.53C234.61,115.8,236,136.12,223.67,152Zm.32,48a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,200Z' />
  </svg>
)

/**
 * PhoneIcon - Phone icon for "Voice Chat" button
 */
const PhoneIcon: React.FC = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 256 256'>
    <path
      d='M164.39,145.34a8,8,0,0,1,7.59-.69l47.16,21.13a8,8,0,0,1,4.8,8.3A48.33,48.33,0,0,1,176,216,136,136,0,0,1,40,80,48.33,48.33,0,0,1,81.92,32.06a8,8,0,0,1,8.3,4.8l21.13,47.2a8,8,0,0,1-.66,7.53L89.32,117a7.93,7.93,0,0,0-.54,7.81c8.27,16.93,25.77,34.22,42.75,42.41a7.92,7.92,0,0,0,7.83-.59Z'
      fill='white'
      stroke='white'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='16'
    />
  </svg>
)

/**
 * IdleStateView - Shows "Voice Chat" button when no active call
 */
const IdleStateView: React.FC<{
  onStartCall: () => void
  brandName?: string
  isLoading: boolean
  primaryColor?: string
}> = ({ onStartCall, brandName = 'Resemble AI', isLoading, primaryColor = '#4ade80' }) => {
  const darkerColor = adjustColorBrightness(primaryColor, -60)

  return (
    <div className='tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-p-4' style={{ '--primary-color': primaryColor, '--darker-color': darkerColor } as React.CSSProperties}>
      <div className='tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3'>
        {/* Animated Gradient Circle */}
        <div className='rotating-gradient-circle tw-flex tw-h-12 tw-w-12 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full' />

        {/* Voice Chat Button */}
        <button
          type='button'
          onClick={onStartCall}
          disabled={isLoading}
          className='tw-flex tw-h-12 tw-flex-1 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-none tw-bg-black tw-px-6 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white tw-transition-all tw-duration-200 hover:tw-scale-105 hover:tw-shadow-lg disabled:tw-cursor-not-allowed disabled:tw-opacity-50'
          aria-label='Start voice chat'
        >
          <PhoneIcon />
          <span>{isLoading ? 'Connecting...' : 'Voice Chat'}</span>
        </button>
      </div>

      {/* Powered By Footer */}
      <div className='tw-mt-2 tw-text-center tw-text-xs tw-text-gray-500'>Powered by {brandName}</div>
    </div>
  )
}

export default VoiceAgentPopup
